/* ----------------------------------------------------------------------------
 * publishConfirmModel — the PURE, DOM-free decision core for the post-prep
 * "shall I publish this draft?" voice loop (#7). It mirrors the scoped-briefing
 * CLARIFY model (wakeMachine.matchModuleAnswer / resolveClarifyAnswer): a
 * CLOSED-SET spoken-answer matcher + a step resolver with a bounded re-ask, so
 * the confirm is DETERMINISTIC and stays OFF the model. Lives here (a runtime-
 * pure module, no react/DOM) so the whole loop is exercised under Node --test
 * (this repo's FE test convention); useVoiceController is the thin glue.
 *
 * SAFETY (locked, invariant 5 + #7 scope): a 'yes' resolves to `approve`, which
 * the controller routes into the EXISTING owner-gated approval primitive
 * (requestApproval -> approve) and ends at the existing approval/queue surface —
 * there is NO publish/connector-push leg here and none is representable. 'edit'
 * opens the existing editor surface; anything not clearly yes/edit (including the
 * shared turn-close set) CANCELS cleanly — ambiguity NEVER publishes.
 *
 * The turn-close fallthrough REUSES the shared isTurnClose (imported, NOT
 * re-implemented) so "no", "nope", "that's all", "stop", "shut it" etc. all
 * close, byte-identical to the follow-up loop's exit.
 * -------------------------------------------------------------------------- */

import { isTurnClose } from '../wake/wakeMachine.ts';
import type { ProposedAction } from '../types.ts';

/** Aura's spoken prompt once a prepared draft card is shown. */
export const PUBLISH_CONFIRM_QUESTION = 'Shall I publish this draft?';
/** The single re-ask when the first answer was not understood. */
export const PUBLISH_CONFIRM_REASK =
  "Sorry, I didn't catch that — shall I publish this draft? You can say yes, edit, or no.";
/** Spoken ack when the user declines / the loop cancels (stays a draft, nothing published). */
export const PUBLISH_CONFIRM_CANCEL_ACK = 'Okay, I’ll leave it as a draft.';
/** Spoken ack when a 'yes' parks the draft in the EXISTING owner approval queue (no publish leg). */
export const PUBLISH_CONFIRM_QUEUED_ACK =
  'Okay — I’ve put it in your approval queue. Nothing is published until you approve it.';
/** Spoken ack when the user chooses to edit — the existing editor surface opens. */
export const PUBLISH_CONFIRM_EDIT_ACK = 'Sure — opening the draft so you can edit it.';

/** The parsed spoken answer to the publish-confirm question (closed set). */
export type PublishConfirmMatch =
  | { kind: 'yes' } // approve -> owner-gated approval primitive
  | { kind: 'edit' } // open the existing editor surface
  | { kind: 'no' } // decline / close -> cancel cleanly (nothing published)
  | { kind: 'unrecognised' }; // nothing in the closed set matched

/** Affirmatives (whole-token contains match, space-padded like matchModuleAnswer). */
const YES_TOKENS: readonly string[] = [
  'yes', 'yeah', 'yep', 'yup', 'sure', 'ok', 'okay', 'okey', 'go ahead', 'do it',
  'publish', 'publish it', 'please do', 'please publish', 'confirm', 'confirmed',
  'go for it', 'yes please', 'affirmative', 'that works', 'sounds good', 'lets do it',
];
/** Edit intent — open the editor surface rather than approve or cancel. */
const EDIT_TOKENS: readonly string[] = [
  'edit', 'change', 'revise', 'amend', 'modify', 'tweak', 'adjust', 'fix',
  'make changes', 'let me edit', 'i want to edit', 'change it', 'edit it', 'edit first',
];
/** Explicit declines NOT already covered by the shared turn-close set. IVG F1
 *  (2026-07-25): bare negations ADDED — any contained negation must beat any
 *  affirmative ("yes... actually no", "yeah nah", "yes definitely not" -> no). */
const NO_TOKENS: readonly string[] = [
  'no', 'nope', 'nah', 'not', 'never', 'negative', 'cancel',
  'not now', 'not yet', 'leave it', 'dont', 'do not', 'dont publish', 'do not publish',
  'later', 'hold on', 'hold off', 'wait', 'keep it a draft', 'keep as draft', 'save as draft',
];
/** HEDGES (IVG F1): uncertainty/question-back markers — an answer containing one
 *  is NEVER a clean yes; it re-asks (once) then cancels. */
const HEDGE_TOKENS: readonly string[] = [
  'maybe', 'perhaps', 'possibly', 'i guess', 'guess', 'unsure', 'hmm', 'um',
  'dunno', 'dont know', 'think about', 'what', 'which', 'why', 'how',
];

/** Normalise a spoken answer the same shape isTurnClose uses: drop apostrophes,
 *  lowercase, non-alnum -> space, collapse, space-pad for whole-token contains. */
function norm(transcript: string): string {
  const s = (transcript ?? '')
    .replace(/['’‘`]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return s === '' ? '' : ' ' + s + ' ';
}

function containsAny(padded: string, tokens: readonly string[]): boolean {
  if (padded === '') return false;
  for (const tok of tokens) {
    if (padded.includes(' ' + tok + ' ')) return true;
  }
  return false;
}

/**
 * Match a spoken answer against the closed publish-confirm set. Precedence is
 * SAFETY-FIRST: a decline/close (the shared turn-close set OR an explicit NO
 * token) is honoured before 'yes', and 'edit' before 'yes', so a mixed or
 * hesitant answer NEVER resolves to publish/approve. A clean affirmative with no
 * decline/edit signal -> 'yes'. Nothing recognised -> 'unrecognised' (re-ask).
 */
export function matchPublishConfirmAnswer(transcript: string): PublishConfirmMatch {
  const padded = norm(transcript);
  if (padded === '') return { kind: 'unrecognised' };
  // SAFETY-FIRST precedence (IVG F1): EDIT > NO > HEDGE > YES. Edit is always a
  // safe resolution (opens the editor, publishes nothing) and honours "no, let
  // me edit it". Any contained negation then beats any affirmative; any hedge/
  // question-back blocks a clean yes (re-ask); only an unhedged, un-negated
  // affirmative resolves to 'yes'.
  if (containsAny(padded, EDIT_TOKENS)) return { kind: 'edit' };
  if (isTurnClose(transcript) || containsAny(padded, NO_TOKENS)) return { kind: 'no' };
  if (containsAny(padded, HEDGE_TOKENS)) return { kind: 'unrecognised' };
  if (containsAny(padded, YES_TOKENS)) return { kind: 'yes' };
  return { kind: 'unrecognised' };
}

/** The next step after parsing an answer, given how many re-asks already happened. */
export type PublishConfirmStep =
  | { kind: 'approve' } // route to the owner-gated approval primitive (ends at existing surface)
  | { kind: 'edit' } // open the existing editor surface
  | { kind: 'cancel' } // leave the draft, close the loop cleanly
  | { kind: 're-ask' }; // ask once more, capture again

/** Maximum re-asks before the loop CANCELS (safe default: never publish on ambiguity). */
export const PUBLISH_CONFIRM_MAX_REASKS = 1;

/**
 * Resolve the loop step. 'yes' -> approve; 'edit' -> edit; 'no' -> cancel;
 * 'unrecognised' -> re-ask once, then CANCEL (the safe fallback — unlike the
 * briefing clarify's "full briefing" fallback, the safe terminal here is to
 * publish NOTHING). Mirrors resolveClarifyAnswer's shape.
 */
export function resolvePublishConfirmAnswer(
  match: PublishConfirmMatch,
  reAskCount: number,
): PublishConfirmStep {
  if (match.kind === 'yes') return { kind: 'approve' };
  if (match.kind === 'edit') return { kind: 'edit' };
  if (match.kind === 'no') return { kind: 'cancel' };
  return reAskCount < PUBLISH_CONFIRM_MAX_REASKS ? { kind: 're-ask' } : { kind: 'cancel' };
}

/** The stable ProposedAction kind for a queued blog draft awaiting owner approval. */
export const PUBLISH_CONFIRM_ACTION_KIND = 'queue_blog_draft_for_approval';

/** The minimal draft shape the loop needs to build a proposal (id + title). */
export interface PublishConfirmDraft {
  id: number | string;
  title?: string;
}

/**
 * Build the ProposedAction a 'yes' parks in the approval primitive. The summary
 * is DELIBERATELY honest about the draft-only outcome: approving sends the draft
 * to the existing owner approval/queue surface — it does NOT publish (there is no
 * publish leg). "AI proposes; a human decides."
 */
export function buildPublishConfirmAction(draft: PublishConfirmDraft): ProposedAction {
  const title = typeof draft.title === 'string' && draft.title.trim() !== '' ? draft.title.trim() : 'this draft';
  return {
    kind: PUBLISH_CONFIRM_ACTION_KIND,
    summary: `Send “${title}” to the approval queue for review. It stays a draft — nothing is published.`,
    payload: { draft_id: draft.id, title, channel: 'blog' },
  };
}
